import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, PauseCircle } from 'lucide-react';
import { format } from 'date-fns';
import { apiClient } from '../../lib/api/config.js';

/**
 * SA-SHADOW week (operator 2026-10-07): the operator's judgement on a story
 * synthesis piece. Release / Hold RECORDS the decision only — nothing is
 * approved, published or posted. A piece is still published manually through
 * the normal controls.
 */
interface ReleaseRecord {
  decision: 'release' | 'hold';
  by: string;
  at: string;
  gate_status_at_decision?: string;
}

interface DailyPick {
  pick_date?: string;
  rank?: number;
  recent_released_members?: number;
  total_released_members?: number;
}

export function SynthesisReleasePanel({ articleId, metadata }: {
  articleId: string;
  metadata: Record<string, unknown> | null | undefined;
}) {
  const queryClient = useQueryClient();
  const release = (metadata?.release ?? null) as ReleaseRecord | null;
  const pick = (metadata?.daily_pick ?? null) as DailyPick | null;
  const mutation = useMutation({
    mutationFn: async (decision: 'release' | 'hold') =>
      (await apiClient.post(`/admin/threads/articles/${articleId}/release`, { decision })).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['article', articleId] }),
  });

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 mb-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Your decision on this piece</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Recorded only — Release does not publish during the shadow week.
            {pick?.pick_date && ` Daily pick ${pick.pick_date}, #${pick.rank}`}
            {pick?.recent_released_members != null && ` · ${pick.recent_released_members} recent members`}
          </p>
          {release && (
            <p className="text-xs text-gray-700 mt-1">
              {release.decision === 'release' ? 'Released' : 'Held'} by {release.by} on{' '}
              {format(new Date(release.at), 'd MMM yyyy, h:mm a')}
            </p>
          )}
          {mutation.isError && (
            <p className="text-xs text-red-600 mt-1">
              {(mutation.error as any)?.response?.data?.detail?.message || 'Could not record the decision'}
            </p>
          )}
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            type="button"
            onClick={() => mutation.mutate('release')}
            disabled={mutation.isPending}
            className={`inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium border ${
              release?.decision === 'release'
                ? 'bg-green-600 text-white border-green-600'
                : 'bg-white text-green-700 border-green-300 hover:bg-green-50'
            }`}
          >
            <CheckCircle2 className="h-4 w-4" /> Release
          </button>
          <button
            type="button"
            onClick={() => mutation.mutate('hold')}
            disabled={mutation.isPending}
            className={`inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium border ${
              release?.decision === 'hold'
                ? 'bg-amber-500 text-white border-amber-500'
                : 'bg-white text-amber-700 border-amber-300 hover:bg-amber-50'
            }`}
          >
            <PauseCircle className="h-4 w-4" /> Hold
          </button>
        </div>
      </div>
    </div>
  );
}
